import { yupResolver } from '@hookform/resolvers/yup';
import { Input, Select } from '@insify/design-system/components';
import { FormContainer, useWizard } from '@insify/funnel-components';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useCommands } from '../../hooks/useCommands';
import { useContent } from '../../hooks/useContent';
import { useConditionsQuery, useParamsQuery } from '../../hooks/useQueries';

import { validation } from './validation';

import type { Conditions } from '../../models/queries';
import type { IsoDate } from '@insify/global-date';
import type { SubmitHandler } from 'react-hook-form';

export type FormType = {
  name: string;
  id: { type: string; value: string };
  foundingDate: IsoDate;
  legalStructureCode: { type: string; value: string };
  funnel: string;
};

const Inputs = ({
  legalStructureCodes,
  submitting,
}: {
  legalStructureCodes: Conditions['legalStructureCodes'];
  submitting: boolean;
}) => {
  const formMethods = useFormContext<FormType>();
  const { t } = useTranslation(['funnel']);

  const codesOptions = legalStructureCodes.map(({ description, value }) => ({
    label: description,
    value,
  }));

  return (
    <>
      <Input
        {...formMethods.register('name')}
        trackingId='name'
        id='name'
        error={formMethods.formState.errors.name?.message}
        label={t('funnel:company_details_name')}
        disabled={submitting}
      />
      <Input
        {...formMethods.register('id.value')}
        trackingId={'siret'}
        id='siret'
        error={formMethods.formState.errors.id?.value?.message}
        label={t('funnel:company_details_siret')}
        disabled={submitting}
      />
      <Select
        {...formMethods.register('legalStructureCode.value')}
        trackingId={'legal-structure-code'}
        id='legal-structure-code'
        error={formMethods.formState.errors.legalStructureCode?.value?.message}
        label={t('funnel:company_details_legal_type')}
        disabled={submitting}
        placeholder={t('funnel:company_details_legal_type_placeholder')}
        required={true}
      >
        {codesOptions.map(({ label, value }) => (
          <option key={label} value={value}>
            {label}
          </option>
        ))}
      </Select>
      <input type='hidden' {...formMethods.register('foundingDate')} />
    </>
  );
};

export function CompanyDetailsPage(): JSX.Element | null {
  const { showPreviousPage, showNextPage } = useWizard();
  const { t } = useTranslation(['common', 'validation', 'funnel']);
  const { quoteWizardCompanyDetails } = useContent() || {};
  const { sendCommand } = useCommands();
  const { isStale: paramsStale, data: quoteParams, isFetched } = useParamsQuery();
  const { data: quoteConditions } = useConditionsQuery();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [triggerShowingNextPage, setTriggerShowingNextPage] = useState(false);
  const [paramsHasBeenRequested, setParamsHasBeenRequested] = useState(false);

  const handleCommand: SubmitHandler<FormType> = async (formData) => {
    setIsSubmitting(true);
    await sendCommand({
      type: 'enterCompanyDetails',
      payload: {
        name: formData.name,
        id: formData.id.value.replace(/\s/g, ''),
        foundingDate: formData.foundingDate,
        legalStructureCode: formData.legalStructureCode.value,
        funnel: 'FR',
        keepActivitiesAndClaims: true,
      },
    });

    setTriggerShowingNextPage(true);
  };

  useEffect(() => {
    if (triggerShowingNextPage && !paramsStale) {
      setParamsHasBeenRequested(true);
    }
  }, [triggerShowingNextPage, paramsStale]);

  useEffect(() => {
    if (triggerShowingNextPage && paramsHasBeenRequested && !paramsStale) {
      showNextPage();
      setIsSubmitting(false);
      setTriggerShowingNextPage(false);
    }
  }, [triggerShowingNextPage, paramsHasBeenRequested, paramsStale]);

  if (!isFetched || !quoteParams || !quoteConditions || !quoteConditions.legalStructureCodes) {
    return null;
  }

  return (
    <FormContainer
      formData={{
        ...quoteParams.company,
        foundingDate: quoteParams.company?.foundingDate ?? quoteParams.foundingDate,
      }}
      inputs={<Inputs legalStructureCodes={quoteConditions.legalStructureCodes} submitting={isSubmitting} />}
      validation={yupResolver(validation(t))}
      eventHandlers={{
        onSubmit: handleCommand,
        onBack: showPreviousPage,
      }}
      content={quoteWizardCompanyDetails}
      translations={{
        back: t('common:back'),
        forward: t('common:continue'),
      }}
      submitting={isSubmitting}
    />
  );
}
